import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('pixzent_admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors globally
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('pixzent_admin_token');
      localStorage.removeItem('pixzent_admin_auth');
      // Only redirect if on admin page
      if (window.location.pathname.includes('/admin')) {
        window.location.href = '/';
      }
    }
    return Promise.reject(error);
  }
);

// ============ TRACKING HELPERS ============

const getUTMParams = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get('utm_source') || null,
    utm_medium: params.get('utm_medium') || null,
    utm_campaign: params.get('utm_campaign') || null,
    utm_term: params.get('utm_term') || null,
    utm_content: params.get('utm_content') || null,
  };
};

const getTrackingData = (source = 'Website') => {
  const utmParams = getUTMParams();
  return {
    source,
    page_url: window.location.href,
    referrer: document.referrer || null,
    ...utmParams,
  };
};

// ============ AUTH API ============

export const authAPI = {
  login: async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },
  
  verify: async () => {
    const response = await api.get('/auth/verify');
    return response.data;
  },
};

// ============ AUDIT SUBMISSIONS API ============

export const auditAPI = {
  // Public - Create submission
  createSubmission: async (data, source = 'Website') => {
    const trackingData = getTrackingData(source);
    
    const payload = {
      full_name: data.fullName || data.full_name,
      business_name: data.businessName || data.business_name || data.fullName || data.full_name || 'Not specified',
      website_url: data.websiteUrl || data.website_url || 'Not specified',
      email: data.email,
      phone: data.phone || '',
      location: data.location || 'Not specified',
      industry: data.industry || 'Not specified',
      challenge: data.challenge || 'Audit request',
      ...trackingData,
    };
    
    const response = await api.post('/audit-submissions', payload);
    return response.data;
  },
  
  // Admin - Get all submissions
  getAllSubmissions: async () => {
    const response = await api.get('/admin/submissions');
    return response.data;
  },
  
  // Admin - Get single submission
  getSubmission: async (id) => {
    const response = await api.get(`/admin/submissions/${id}`);
    return response.data;
  },
  
  // Admin - Update submission
  updateSubmission: async (id, data) => {
    const response = await api.patch(`/admin/submissions/${id}`, data);
    return response.data;
  },
  
  // Admin - Delete submission
  deleteSubmission: async (id) => {
    const response = await api.delete(`/admin/submissions/${id}`);
    return response.data;
  },
  
  // Admin - Get stats
  getStats: async () => {
    const response = await api.get('/admin/stats');
    return response.data;
  },
};

// ============ HEALTH API ============

export const healthAPI = {
  check: async () => {
    const response = await api.get('/health');
    return response.data;
  },
};

export default api;
